import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stack,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  SportsEsports as SportsEsportsIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import useNotifications from '../hooks/useNotifications';
import PendingMatchCard from '../components/PendingMatchCard';
import DenyMatchCard from '../components/DenyMatchCard';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../components/PageTitle';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const { 
    notificationCount, 
    matches = [], 
    rejectedMatches = [], 
    usersMap = {}, 
    loading: isLoading = false,
    handleApprove = () => {}, 
    handleReject = () => {}, 
    fetchPendingMatches = () => {},
    handleOk = () => {},
    handleCreateNew = () => {}
  } = useNotifications();

  useEffect(() => {
    if (!isLoading && initialLoad) {
      if (matches.length === 0) {
        setActiveTab(1);
      }
      setInitialLoad(false);
    }
  }, [isLoading, matches.length, initialLoad]);

  const handleMatchClick = async (e, match) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/match/${match.id}`);
  };

  const handleUpdate = async () => {
    await fetchPendingMatches();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setInitialLoad(false);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (activeTab === 0 && matches.length === 0 && rejectedMatches.length === 0) {
      return (
        <Box sx={{ 
          py: 4,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2
        }}>
          <SportsEsportsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Box sx={{ textAlign: 'center' }}>
            <PageTitle title="No hay notificaciones pendientes" />
            <Button 
              variant="outlined" 
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Volver al inicio
            </Button>
          </Box>
        </Box>
      );
    }

    if (activeTab === 0 && matches.length === 0) {
      return (
        <Box sx={{ 
          py: 4,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2
        }}>
          <SportsEsportsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Box sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '1rem'
          }}>
            No hay partidas pendientes de aprobación
          </Box>
        </Box>
      );
    }

    if (activeTab === 1 && rejectedMatches.length === 0) {
      return (
        <Box sx={{ 
          py: 4,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2
        }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Box sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '1rem'
          }}>
            No hay partidas rechazadas
          </Box>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {activeTab === 0 ? (
          matches.map((match) => (
            <PendingMatchCard
              key={`pending-${match.id}`}
              match={match}
              usersMap={usersMap}
              onClick={handleMatchClick}
              onUpdate={handleUpdate}
              showApprovalButtons={true}
            />
          ))
        ) : (
          rejectedMatches.map((match) => (
            <DenyMatchCard
              key={`rejected-${match.id}`}
              match={match}
              onUpdate={handleUpdate}
              onClick={handleMatchClick}
            />
          ))
        )}
      </Stack>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <PageTitle title={`Notificaciones ${notificationCount > 0 ? `(${notificationCount})` : ''}`} />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem'
            }
          }}
        >
          <Tab 
            icon={
              <Badge 
                badgeContent={matches.length} 
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: '16px',
                    minWidth: '16px'
                  }
                }}
              >
                <NotificationsIcon />
              </Badge>
            } 
            label="Partidas Pendientes" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={
              <Badge 
                badgeContent={rejectedMatches.length} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: '16px',
                    minWidth: '16px'
                  }
                }}
              >
                <WarningIcon />
              </Badge>
            } 
            label="Partidas Rechazadas" 
            sx={{ textTransform: 'none', }}
          />
        </Tabs>
      </Box>

      {renderContent()}
    </Container>
  );
};

export default NotificationsPage; 